import { useContext, useMemo, useState } from 'react';
import { useMutation } from '@apollo/client';
import * as R from 'ramda';

import DeleteModal from '@atom/components/common/DeleteModal';
import TaskItemTile from '@atom/components/common/workOrderDetail/taskItemTile/TaskItemTile';
import WorkOrderContext from '@atom/components/workOrderDetail/WorkOrderContext';
import { TASK_ASSET_REMOVE } from '@atom/graph/task';
import { useNavigation } from '@atom/hooks/useNavigation';
import { usePreferences } from '@atom/hooks/usePreferences';
import { useSchemas } from '@atom/hooks/useSchemas';
import { useWorkValidations } from '@atom/hooks/useWorkValidations';
import { Icon, IconButton, Snackbar } from '@atom/mui';
import { TaskAssetRemoveInput } from '@atom/types/task';
import {
  doesNotHaveRolePermissions,
  ROLE_SETS,
} from '@atom/utilities/authUtilities';
import iconUtilities from '@atom/utilities/iconUtilities';
import schemaUtilities from '@atom/utilities/schemaUtilities';

import classes from './assetSection.module.css';

interface Props {
  assetId: string;
}

const styles = {
  icon: {
    height: '24px',
    width: '24px',
  },
};

const AssetTile = ({ assetId }: Props) => {
  const [, { navigate }] = useNavigation();
  const preferences = usePreferences();
  const navigateToAssetDetail = R.pathOr(
    false,
    ['workOrders', 'navigateToAssetDetail'],
    preferences,
  );
  const { schemas = [] } = useSchemas();

  const { workOrderDetail, refetch, task, setActiveWorkOrderAsset } =
    useContext(WorkOrderContext);

  const { workValidations } = useWorkValidations();

  const [open, setOpen] = useState<boolean>(false);

  const rootSchema = useMemo(() => {
    return R.find(
      R.propEq(workOrderDetail.inventoryAssetSchemaId, 'id'),
      schemas,
    );
  }, [workOrderDetail, schemas]);

  const [deleteAsset] = useMutation<
    { taskAssetRemove: boolean },
    { input: TaskAssetRemoveInput }
  >(TASK_ASSET_REMOVE);

  const removeAsset = async () => {
    setOpen(false);

    Snackbar.info({
      message: 'Deleting...',
    });

    await deleteAsset({
      variables: {
        input: {
          workOrderId: workOrderDetail.id,
          taskId: task.id,
          assetId,
        },
      },
    });

    Snackbar.info({
      message: `Removed Asset.`,
    });

    refetch();
  };

  const asset = workOrderDetail.assets[assetId];
  const hasPendingChanges = R.pathOr(false, ['hasPendingChanges'])(asset);
  const isEditDisabled =
    workOrderDetail.isClosed || doesNotHaveRolePermissions(ROLE_SETS.INSPECTOR);

  const handleClickAsset = (): void => {
    if (navigateToAssetDetail) {
      // If this is a root asset, go to that inventory page
      // - otherwise, go to the subItems tab of the rootAncestorAsset inventory page
      if (R.isEmpty(asset.ancestors)) {
        navigate(`/inventory/${assetId}`);
      } else {
        const rootAncestorAssetId = asset.ancestors[0]?.id;
        if (rootAncestorAssetId) {
          navigate(`/inventory/${rootAncestorAssetId}?activeView=subItems`);
        }
      }
      return;
    }
    setActiveWorkOrderAsset(assetId);
  };

  const icon = (
    <div className={classes.changeIndicatorContainer}>
      {workOrderDetail.inventoryAssetId === assetId
        ? schemaUtilities.getSchemaIconFromSchemaOrAsset(rootSchema, {
            imgOffset: '0',
            iconSize: '24px',
            marginTop: '0',
          })
        : iconUtilities.getDataManagementElementIcon(null, false, styles.icon)}
      {hasPendingChanges && <div className={classes.changeIndicator} />}
    </div>
  );

  const actionButton = isEditDisabled ? (
    <div />
  ) : (
    <IconButton onClick={() => setOpen(true)} size="small" tooltip="Remove">
      <Icon>close</Icon>
    </IconButton>
  );

  const hasError = R.has(assetId)(
    R.pathOr(
      {},
      ['taskValidations', task.id, 'invalidAssets'],
      workValidations,
    ),
  );

  return (
    <>
      <TaskItemTile
        content={R.pathOr('', ['name'], asset)}
        onTileClick={handleClickAsset}
        icon={icon}
        actionButton={actionButton}
        hasError={hasError}
      />
      <DeleteModal
        open={open}
        onConfirm={removeAsset}
        onCancel={() => setOpen(false)}
        title="Remove Asset From Task"
        content="Are you sure you want to remove this asset from the task?"
        cancelText="Cancel"
        confirmText="Remove"
      />
    </>
  );
};

export default AssetTile;
